"use client"

import * as React from "react"
import { DropdownMenu as DropdownMenuPrimitive, Slot } from "radix-ui"
import { CheckIcon, ChevronRightIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { triggerHaptic } from "../../lib/haptics"
import { useIsMobile } from "../../lib/use-mobile"
import { DrawerTrigger } from "./drawer"
import {
  MOBILE_PICKER_CARD_CLASS,
  MOBILE_PICKER_GROUP_LABEL_CLASS,
  MOBILE_PICKER_ROW_CLASS,
  MobilePickerSheetFrame,
} from "./mobile-picker"

/*
 * On a phone a menu is the app's bottom sheet list, not a popover pinned to its trigger: the same sheet every
 * phone picker uses (title, Done, one rounded card of rows). Callers keep composing the Radix parts; each part
 * reads this context and draws its sheet form instead. A larger screen is the unchanged Radix menu.
 */
type MobileMenuContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  close: () => void
}

const MobileMenuContext = React.createContext<MobileMenuContextValue | null>(null)
const MobileRadioContext = React.createContext<{ value?: string; onValueChange?: (value: string) => void } | null>(null)

function useMobileMenu() {
  return React.useContext(MobileMenuContext)
}

/**
 * Runs an item's `onSelect` the way Radix does: a cancelable event, so a caller that calls `preventDefault()`
 * (to open a dialog from the menu, or keep a checkbox list open) keeps the sheet open too.
 */
function runSelect(onSelect: ((event: Event) => void) | undefined, close: () => void) {
  const event = new Event("ibada.menu.select", { cancelable: true })
  onSelect?.(event)
  if (!event.defaultPrevented) close()
}

const MOBILE_ITEM_CLASS = cn(MOBILE_PICKER_ROW_CLASS, "border-b border-border/60")

type DropdownMenuMobileItemRenderer = (
  item: {
    key: string
    children: React.ReactNode
    disabled?: boolean
    className?: string
    inset?: boolean
    variant?: "default" | "destructive"
    checked?: boolean | "indeterminate"
  },
  state: { selected: boolean; disabled: boolean }
) => React.ReactNode

function DropdownMenu({
  mobileMode = "auto",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root> & {
  /** "auto" (default): a bottom sheet on a phone, anchored elsewhere. "anchored": always pinned to the trigger. */
  mobileMode?: "sheet" | "anchored" | "auto"
}) {
  const isMobile = useIsMobile()
  const sheet = mobileMode === "sheet" || (mobileMode === "auto" && isMobile)
  if (!sheet) return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
  return <MobileDropdownMenu {...props} />
}

function MobileDropdownMenu({
  open: openProp,
  defaultOpen,
  onOpenChange,
  children,
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  const [uncontrolled, setUncontrolled] = React.useState(defaultOpen ?? false)
  const open = openProp ?? uncontrolled
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setUncontrolled(next)
      onOpenChange?.(next)
    },
    [onOpenChange, openProp],
  )
  const value = React.useMemo(() => ({ open, setOpen, close: () => setOpen(false) }), [open, setOpen])
  return <MobileMenuContext.Provider value={value}>{children}</MobileMenuContext.Provider>
}

function DropdownMenuPortal({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  const mobile = useMobileMenu()
  if (mobile) return <>{props.children}</>
  return <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
}

function DropdownMenuTrigger({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  const mobile = useMobileMenu()
  if (mobile) {
    const { asChild, children, disabled, ...rest } = props
    const Comp = asChild ? Slot.Root : "button"
    return (
      <Comp
        data-slot="dropdown-menu-trigger"
        {...(asChild ? {} : { type: "button" as const })}
        aria-haspopup="dialog"
        aria-expanded={mobile.open}
        data-state={mobile.open ? "open" : "closed"}
        disabled={disabled}
        className={cn("cursor-pointer data-disabled:cursor-not-allowed", className)}
        {...(rest as React.ComponentProps<"button">)}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          ;(rest as React.ComponentProps<"button">).onClick?.(event)
          if (!event.defaultPrevented) mobile.setOpen(true)
        }}
      >
        {children}
      </Comp>
    )
  }
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      className={cn("cursor-pointer data-disabled:cursor-not-allowed", className)}
      {...props}
    />
  )
}

function DropdownMenuContent({
  className,
  align = "start",
  sideOffset = 4,
  mobileItemRenderer: _mobileItemRenderer,
  mobileEmpty: _mobileEmpty,
  mobileTitle: _mobileTitle,
  mobileDescription: _mobileDescription,
  mobileCloseLabel: _mobileCloseLabel,
  mobileClassName: _mobileClassName,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content> & {
  /** @deprecated Ignored: items draw their own sheet rows. */
  mobileItemRenderer?: DropdownMenuMobileItemRenderer
  /** The phone sheet's title (the menu's subject, e.g. "Actions"). */
  mobileEmpty?: React.ReactNode
  mobileTitle?: React.ReactNode
  mobileDescription?: React.ReactNode
  mobileCloseLabel?: string
  mobileClassName?: string
}) {
  void _mobileItemRenderer
  const mobile = useMobileMenu()

  if (mobile) {
    const hasItems = React.Children.toArray(props.children).length > 0
    return (
      <MobilePickerSheetFrame
        open={mobile.open}
        onOpenChange={mobile.setOpen}
        title={_mobileTitle ?? ""}
        description={_mobileDescription}
        doneLabel={_mobileCloseLabel}
      >
        <div className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto", _mobileClassName)}>
          {hasItems ? (
            // `-mb-px` tucks the last row's hairline under the card's rounded edge.
            <div role="menu" className={MOBILE_PICKER_CARD_CLASS}>
              <div className="-mb-px flex flex-col">{props.children}</div>
            </div>
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm font-semibold">{_mobileEmpty}</p>
          )}
        </div>
      </MobilePickerSheetFrame>
    )
  }
  void _mobileEmpty
  void _mobileTitle
  void _mobileDescription
  void _mobileCloseLabel
  void _mobileClassName

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        align={align}
        className={cn(
          "ring-border/80 bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) min-w-32 origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg p-1 shadow-[var(--shadow-popover)] ring-1 duration-100 data-[state=closed]:overflow-hidden",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  const mobile = useMobileMenu()
  if (mobile) return <div role="group" className="flex flex-col">{props.children}</div>
  return (
    <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
  )
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean
  variant?: "default" | "destructive"
}) {
  const mobile = useMobileMenu()
  if (mobile) {
    const { asChild, children, disabled, onSelect, onClick, textValue: _textValue, ...rest } = props
    void _textValue
    const Comp = asChild ? Slot.Root : "button"
    return (
      <Comp
        data-slot="dropdown-menu-item"
        data-variant={variant}
        role="menuitem"
        {...(asChild ? {} : { type: "button" as const, disabled })}
        aria-disabled={disabled || undefined}
        className={cn(
          MOBILE_ITEM_CLASS,
          variant === "destructive" && "text-destructive [&_svg]:text-destructive",
          disabled && "pointer-events-none opacity-55",
          className,
        )}
        {...(rest as React.ComponentProps<"button">)}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          ;(onClick as React.MouseEventHandler<HTMLButtonElement> | undefined)?.(event)
          if (event.defaultPrevented || disabled) return
          triggerHaptic("selection")
          runSelect(onSelect, mobile.close)
        }}
      >
        {children}
      </Comp>
    )
  }
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:text-destructive not-data-[variant=destructive]:focus:**:text-accent-foreground group/dropdown-menu-item relative flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-50 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem> & {
  inset?: boolean
}) {
  const mobile = useMobileMenu()
  if (mobile) {
    const { disabled, onCheckedChange, onSelect } = props
    return (
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={checked === "indeterminate" ? "mixed" : Boolean(checked)}
        disabled={disabled}
        className={cn(MOBILE_ITEM_CLASS, className)}
        onClick={() => {
          triggerHaptic("selection")
          onCheckedChange?.(checked !== true)
          runSelect(onSelect, mobile.close)
        }}
      >
        <span className="flex min-w-0 flex-1 items-center gap-3">{children}</span>
        {checked ? <CheckIcon aria-hidden className="text-primary size-5" strokeWidth={3} /> : null}
      </button>
    )
  }
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground relative flex cursor-pointer items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-50 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      checked={checked}
      {...props}
    >
      <span
        className="pointer-events-none absolute right-2 flex items-center justify-center"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  const mobile = useMobileMenu()
  if (mobile) {
    return (
      <MobileRadioContext.Provider value={{ value: props.value, onValueChange: props.onValueChange }}>
        <div role="group" className="flex flex-col">{props.children}</div>
      </MobileRadioContext.Provider>
    )
  }
  return (
    <DropdownMenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem> & {
  inset?: boolean
}) {
  const mobile = useMobileMenu()
  const radio = React.useContext(MobileRadioContext)
  if (mobile) {
    const { disabled, onSelect, value } = props
    const selected = radio?.value === value
    return (
      <button
        type="button"
        role="menuitemradio"
        aria-checked={selected}
        disabled={disabled}
        className={cn(MOBILE_ITEM_CLASS, className)}
        onClick={() => {
          triggerHaptic("selection")
          radio?.onValueChange?.(value)
          runSelect(onSelect, mobile.close)
        }}
      >
        <span className="flex min-w-0 flex-1 items-center gap-3">{children}</span>
        {selected ? <CheckIcon aria-hidden className="text-primary size-5" strokeWidth={3} /> : null}
      </button>
    )
  }
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground relative flex cursor-pointer items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-50 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <span
        className="pointer-events-none absolute right-2 flex items-center justify-center"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean
}) {
  const mobile = useMobileMenu()
  if (mobile) {
    return (
      <div className={cn(MOBILE_PICKER_GROUP_LABEL_CLASS, "border-b border-border/60 bg-card/60 px-4", className)}>
        {props.children}
      </div>
    )
  }
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "text-muted-foreground px-1.5 py-1 text-xs font-medium data-inset:pl-7",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  const mobile = useMobileMenu()
  // Rows already carry a hairline; a gap reads as the break between groups.
  if (mobile) return <div role="separator" className="bg-card h-2 border-b border-border/60" />
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  // Keyboard shortcuts mean nothing on a phone.
  if (useMobileMenu()) return null
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground ml-auto text-xs tracking-widest",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSub({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  // A phone has no room for a flyout: the submenu's items sit in the sheet under their heading.
  if (useMobileMenu()) return <>{props.children}</>
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean
}) {
  const mobile = useMobileMenu()
  if (mobile) {
    return (
      <div className={cn(MOBILE_PICKER_GROUP_LABEL_CLASS, "flex items-center gap-2 border-b border-border/60 bg-card/60 px-4 [&_svg]:size-4", className)}>
        {children}
      </div>
    )
  }
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-open:bg-accent data-open:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-sm outline-hidden select-none data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto" />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  if (useMobileMenu()) return <div className="flex flex-col">{props.children}</div>
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn("data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-border/80 bg-popover text-popover-foreground z-50 min-w-[96px] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-lg p-1 shadow-[var(--shadow-popover)] ring-1 duration-100", className )}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
}
